import React, { useEffect, useState } from 'react';
import './App.css';
import SessionStart from './components/SessionStart';
import ExistingSessions from './components/ExistingSessions';
import LoadSessions from './components/LoadSessions';
import SSO from './components/SSO';
import Session from './components/Session';
import Portal from './components/Portal';
import TaskmanSetup from './components/TaskmanSetup';
import { AppProvider } from './contexts/AppContext';
import { Modal } from '@mui/material';
import { app_start, update_app_auth } from './mobile/mobileHandler';
import { useSearchParams } from "react-router-dom";
import { Cross2Icon } from './icons/Icons';
import { refreshTokenSilently, msalInstance } from './utils/AuthUtilities';
import { ISDLUrl, ISDLFunctionsKey } from './isdlData/isdlData';
import ErrorModal from './components/ErrorModal';
import { AppStateService } from './contexts/AppState';

function App() {
  const [currentPage, setCurrentPage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [sessionIndex, setSessionIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState("");

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalHeader, setErrorModalHeader] = useState("");
  const [errorModalMessage, setErrorModalMessage] = useState("");

  const [searchParams] = useSearchParams();

  const [currentTaskId, setCurrentTaskId] = useState("");
  const [currentHeadId, setCurrentHeadId] = useState("");

  const [canCloseModal, setCanCloseModal] = useState(true);
  const [taskmanIndex, setTaskmanIndex] = useState(0);

  const [childCloseHandler, setChildCloseHandler] = useState(null);

    const [theme, setTheme] = useState(
    () => localStorage.getItem('theme') || 'dark'
  );

  let init = false;

  useEffect(() => {
    // const inApp = searchParams.get('App');

    window.addEventListener('requestForNewToken', requestNewToken);

    app_start(ISDLUrl(), ISDLFunctionsKey());

    setCurrentPage("SSO");

    window.addEventListener('currentSessionUpdate', currentSessionUpdate);
    window.addEventListener('upsertSessionError', handleErrorModalPopup);
    window.addEventListener('usesApp', initApp);

    return () => {
      window.removeEventListener('barcodeScanValue',handleNewScan);
      window.removeEventListener('requestForNewToken',requestNewToken);
      window.removeEventListener('upsertSessionError', handleErrorModalPopup);
      window.removeEventListener('usesApp', initApp);
    }
  }, []);

    useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const currentSessionUpdate = () => {
    setSessionIndex(prevKey => prevKey + 1);
  }

  const handleErrorModalPopup = (details) => {
    const data = details.detail;
    if(data.header){
      setErrorModalHeader(data.header);
    }else{
      setErrorModalHeader("Error");
    }

    if(data.type === "412"){
      setErrorModalHeader("Session merge conflict");
    }

    setErrorModalMessage(data.message);
    setErrorModalOpen(true);
  }

  const handleErrorModalResult = () => {
    setErrorModalOpen(false);
  }

  const handleLoadSession = async (user, page) => {
    if(user){
      setSelectedUser(user);
    }

    if(page === "MAINWINDOW"){
      setSessionIndex(prevKey => prevKey + 1);
    }

    if(page === "PORTAL"){
      const authToken = sessionStorage.getItem('authToken');
      const apiKey = process.env.REACT_APP_API_KEY;
      update_app_auth(authToken, apiKey, ISDLUrl(), ISDLFunctionsKey());
    }
    
    if (currentModal === "TASKMAN") {
      setCurrentModal("");
      setCurrentTaskId("");
      setCurrentHeadId("");
    }

    setModalOpen(false);
    setCurrentPage(page);
  };

  const closeTaskmanModal = () => {
    setModalOpen(false);
    setCurrentModal("");
    setCurrentTaskId("");
    setCurrentHeadId("");
  };

  const handleOpenModal = async (user, page) =>{
    if(user){
      // localStorage.setItem('savedUser',user.value);
      setSelectedUser(user);
    }

    setCurrentModal(page);
    setModalOpen(true);
  }

  const handleModalClose = () =>{
    switch(currentModal){
      case "START":
        setModalOpen(false);
        break;
      case "EXISTINGSESSION":
        setModalOpen(false);
        break;
      case "TASKMAN":
        childCloseHandler();
        break;
      default:
        setModalOpen(false);
        break;
    }
  }

  const registerCloseHandler = (handler) => {
    setChildCloseHandler(() => handler);
  };

  const initApp = (data) => {
    if (data.detail.hasOwnProperty('appVersion') && data.detail.appVersion &&
      data.detail.hasOwnProperty('appEnvironment') && data.detail.appEnvironment) {
      AppStateService.setInAppInfo({
        appVersion: data.detail.appVersion,
        appEnvironment: data.detail.appEnvironment,
      });
    }else{
      AppStateService.setInAppInfo({
        appVersion: '0.0.0.0',
        appEnvironment: 'NA'
      });
    }
  }

  // const InitApp = async () => {

  //   if(init)//prevents some double calling. not sure why? UPDATE: debugging react.js does it for some reason. Not in final build!
  //     return;

  //   const inApp = searchParams.get('App');

  //   if(inApp){
  //     app_start();
  //     window.removeEventListener('usesApp', usesApp);
  //     window.addEventListener('usesApp', usesApp);
  //   }else{
  //     init = true;
  //   }

    
  // }

  function handleNewScan(data){
    const newCode = JSON.parse(data.detail).code;
    const regexTaskId = /^\d{4}-\d{5,6}-\d{2}$/; // Pattern: XXXX-XXXXX-XX
      const regexHeadId = /^HD-\d{7}$/;            // Pattern: HD-XXXXXXX

      if (regexTaskId.test(newCode)) {
        setCurrentTaskId(newCode);
        setCurrentHeadId("");
      } else if (regexHeadId.test(newCode)) {
        setCurrentHeadId(newCode);
        setCurrentTaskId("");
      }

      setTaskmanIndex(prevKey => prevKey + 1); //force taskman page to update
  }

  async function requestNewToken(){
    const newToken = await refreshTokenSilently();
    const apiKey = process.env.REACT_APP_API_KEY;
    update_app_auth(newToken, apiKey, ISDLUrl(), ISDLFunctionsKey());
  }

  const handleLogout = async () => {
    setModalOpen(false);
    setCurrentModal("");
    setSelectedUser(null);
    setSessionIndex(0);
    setCurrentTaskId("");
    setCurrentHeadId("");
    setCanCloseModal(true);
    setTaskmanIndex(0);

    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('dataCollectorEmail');
    sessionStorage.removeItem('dataCollectorRole');

    update_app_auth();
    setCurrentPage("SSO");

    try {
      const activeAccount = msalInstance.getActiveAccount();
      if (activeAccount) {
        await msalInstance.logoutRedirect({ account: activeAccount });
      } else {
        await msalInstance.logoutRedirect();
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      msalInstance.setActiveAccount(null);
    }
  };

  function renderSwitch(){
    switch(currentPage)
    {
      case "SSO":
        return <SSO OnSSOFinished={handleLoadSession}/>
      case "PORTAL":
        return <Portal OnModalSwitch={handleOpenModal} OnLogout={handleLogout}/>
      case "START":
        return  <SessionStart dbInitialized={true} onLoadSession={handleLoadSession} />;
      case "EXISTINGSESSION":
        return  <ExistingSessions dataCollectorId={selectedUser.id} onLoadSession={handleLoadSession} />;
      case "MAINWINDOW": 
        return <Session onLoadSession={handleLoadSession} onLoadModal={handleOpenModal} index={sessionIndex}/>;
      case "TASKMAN":
        return <TaskmanSetup HandleTaskSetup={handleLoadSession}/>
      default:
        return null;
    }
  }

  function renderModal(){
    switch(currentModal)
    {
      case "START":
        return  <SessionStart dbInitialized={true} onLoadSession={handleLoadSession} />;
      case "EXISTINGSESSION":
        return  <LoadSessions dataCollector={selectedUser} onLoadSession={handleLoadSession} />;
      case "TASKMAN":
        return <TaskmanSetup taskId={currentTaskId} headPrototypeId={currentHeadId} HandleTaskSetup={handleLoadSession} taskmanIndex={taskmanIndex} registerCloseHandler={registerCloseHandler} parentClose={closeTaskmanModal}/>//4074-76331-10//HD-1644032
      default:
        return null;
    }
  }



  return (
    <AppProvider>
      <div className="App">
          {renderSwitch()}
          <Modal
            open={modalOpen}>
            <div className='portal-modal-container' style={{height:'calc(100vh - 50px)', maxWidth:'1024px', width:'calc(100vw - 50px)'}}>
              <button disabled={!canCloseModal} className='modal-close-button' onClick={() => handleModalClose()}><Cross2Icon width='30px' height='30px'/></button>
              {renderModal()}
            </div>
          </Modal>
          <Modal open={errorModalOpen}>
            <ErrorModal header={errorModalHeader} message={errorModalMessage} errorAcknowledgeResult={handleErrorModalResult}/>
          </Modal>
      </div>
    </AppProvider>
    
  );
}

export default App;
